import React, { FC, useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { distanceInMiles, getWalkingAndDrivingDistance, WALKING_THRESHOLD } from '@shared/utils/distanceInMiles';
import { BoxProps, Flex, TextV2 } from '@withjoy/joykit';
import { WalkIcon } from '@assets/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { Car } from '@withjoy/joykit/icons';
import { TravelDistancesBetweenPointsResponseFragment } from '@graphql/generated';
import { useFormatTravelDistance } from '@shared/utils/hooks/useFormatTravelDistance';
import { extractTypedDistances } from '@shared/utils/formatting/distance.formatter';

interface TravelLogisticsProps
  extends Readonly<{
      distances?: Maybe<TravelDistancesBetweenPointsResponseFragment>;
      venueLocation?: Readonly<{ lat: number; lng: number }> | null;
      hotelLocation?: Readonly<{ lat: number; lng: number }>;
      useShortWording?: boolean;
    }>,
    BoxProps {}

export const TravelLogistics: FC<TravelLogisticsProps> = ({ distances, venueLocation, hotelLocation, useShortWording = false, ...props }) => {
  const { tq } = useTranslation('hotelTile');
  const formatTravelDistance = useFormatTravelDistance();

  const travelInfo = useMemo(() => {
    // Prefer backend-calculated distances if available
    if (distances) {
      const { isWalkable, displayDistance } = extractTypedDistances(distances.commonDistance);

      if (!displayDistance?.durationMinutes) return null;

      return {
        isWalkable,
        walkingMinutes: displayDistance.durationMinutes,
        drivingMinutes: displayDistance.durationMinutes,
      };
    }

    // Fallback: calculate from coordinates
    if (!hotelLocation) return null;

    const distanceFromTheVenue = distanceInMiles(venueLocation, hotelLocation);

    if (!distanceFromTheVenue) return null;

    const travelTimes = getWalkingAndDrivingDistance(distanceFromTheVenue);

    // Use distance threshold to determine walkability (consistent with getTravelDistance)
    return {
      isWalkable: distanceFromTheVenue <= WALKING_THRESHOLD,
      walkingMinutes: travelTimes.walking,
      drivingMinutes: travelTimes.driving,
    };
  }, [distances, venueLocation, hotelLocation]);

  if (!travelInfo) return null;

  // Get display travel walking distance in minutes
  const { formatted: formattedWalkingDistance, unitLabel: walkingDistanceUnitLabel } = formatTravelDistance({
    value: travelInfo.walkingMinutes,
    type: 'time',
  });
  const displayWalkingDistance = useShortWording ? tq`${formattedWalkingDistance} ${walkingDistanceUnitLabel}` : tq`${formattedWalkingDistance} ${walkingDistanceUnitLabel} walk`;

  // Get display travel driving distance in minutes
  const { formatted: formattedDriveDistance, unitLabel: driveDistanceUnitLabel } = formatTravelDistance({
    value: travelInfo.drivingMinutes,
    type: 'time',
  });
  const displayDriveDistance = useShortWording ? tq`${formattedDriveDistance} ${driveDistanceUnitLabel}` : tq`${formattedDriveDistance} ${driveDistanceUnitLabel} drive`;

  return (
    <Flex
      width="fit-content"
      alignItems="center"
      {...props}
    >
      {travelInfo.isWalkable ? (
        <WalkIcon color="mono10" />
      ) : (
        <Car
          size={16}
          color="mono10"
        />
      )}

      <TextV2
        typographyVariant="label2"
        fontFamily="Inter UI"
        color="mono10"
        lineHeight={1.5}
        marginLeft={1}
        fontSize={13}
        maxWidth={{ _: pxToRem(180), sm: pxToRem(260) }}
        truncate
      >
        {travelInfo.isWalkable ? displayWalkingDistance : displayDriveDistance}
      </TextV2>
    </Flex>
  );
};
